import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

export default function FAQPage() {
  const { lang } = useLanguage();
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      q: {
        da: 'Hvor lang tid varer en behandling og hvad koster det?',
        en: 'How long does a treatment last and what are the prices?'
      },
      a: {
        da: 'Alle behandlinger er baseret på tid. Du kan vælge mellem 60 minutter (700 DKK), 75 minutter (860 DKK), 90 minutter (1.000 DKK) og 120 minutter (1.300 DKK). Ved køb af klippekort med 5 behandlinger sparer du 15% (udløber efter 1 år). Alle behandlinger starter med en kort samtale om dine behov og spændinger.',
        en: 'All treatments are time-based. You can choose between 60 minutes (700 DKK), 75 minutes (860 DKK), 90 minutes (1,000 DKK), and 120 minutes (1,300 DKK). Save 15% by purchasing a 5-session package (valid for 1 year). Every session begins with a short consultation.'
      }
    },
    {
      q: {
        da: 'Er Jette RAB-godkendt behandler?',
        en: 'Is Jette an accredited RAB therapist?'
      },
      a: {
        da: 'Ja, Jette er RAB godkendt - Anerkendt af sundhedsvæsenet. RAB (Registreret Alternativ Behandler) er en beskyttet titel, som stiller strenge krav til grunduddannelse, klinisk praksis, etik og løbende efteruddannelse. Dette sikrer dig professionel og tryg behandling. Har du en sundhedsforsikring, kan der udstedes bilag til refusion.',
        en: 'Yes, Jette er RAB godkendt - Anerkendt af sundhedsvæsenet. RAB is a protected quality designation recognized by the Danish healthcare system ensuring high standards of training, ethics, and ongoing education. If you have private health insurance, a receipt can be provided for reimbursement.'
      }
    },
    {
      q: {
        da: 'Hvilken massage eller behandling skal jeg vælge?',
        en: 'Which massage or therapy should I choose?'
      },
      a: {
        da: 'Hvis du har dybe muskelknuder eller triggerpunkter, anbefales Bindevævsmassage eller Neuromuskulær massage. Til aktive og motionister er Sportsmassage ideel, mens Velværemassage giver ren ro og afstresning. Ønsker du dæmpning af stress eller nervesystemsafspænding, er Biodynamisk Kranio-Sakral Terapi ideel. Du er også altid velkommen til blot at booke en tid, så tilpasser vi behandlingen på dagen.',
        en: 'If you suffer from deep knots or trigger points, Deep Tissue or Neuromuscular Massage is recommended. For active individuals, Sports Massage is ideal, while Wellness Massage provides gentle relaxation. For deep nervous system calming, Bio Dynamic Craniosacral Therapy is ideal. We can also customize the session together at your appointment.'
      }
    },
    {
      q: {
        da: 'Tilbydes der gravidmassage og efterfødselsmassage?',
        en: 'Do you offer pregnancy and post-partum massage?'
      },
      a: {
        da: 'Ja, absolut! Gravidmassage og efterfødselsmassage er to af mine store specialer. Behandlingen foregår i behagelige sideleje-positioner med specialdesignede støttepuder.',
        en: 'Yes, absolutely! Pregnancy and postpartum massage are my core specialties, performed with supportive side-lying cushions.'
      }
    },
    {
      q: {
        da: 'Hvordan foregår udekørende behandlinger?',
        en: 'How do mobile / out-call treatments work?'
      },
      a: {
        da: 'Jeg tilbyder udekørende behandlinger direkte i dit hjem eller din virksomhed i postnummer 2300 (Amager) samt efter aftale i Storkøbenhavn. Der tillægges et mindre kørselsgebyr (f.eks. + 300 DKK / efter aftale).',
        en: 'I offer out-call sessions in your home or workspace in zip code 2300 (Amager) and Greater Copenhagen by appointment. A travel fee applies.'
      }
    },
    {
      q: {
        da: 'Hvordan betaler jeg for behandlingen?',
        en: 'What payment options are accepted?'
      },
      a: {
        da: 'Du kan betale med MobilePay, kortbetaling eller kontant i forbindelse med behandlingen.',
        en: 'You can pay via MobilePay, credit/debit card, or cash at the time of your appointment.'
      }
    },
    {
      q: {
        da: 'Skal jeg medbringe noget til behandlingen?',
        en: 'Do I need to bring anything to my session?'
      },
      a: {
        da: 'Nej, du behøver ikke medbringe noget. Klinikken sørger for rene, økologiske lagner og håndklæder.',
        en: 'No, everything is provided including organic clean sheets and towels.'
      }
    }
  ];

  return (
    <div className="space-y-0">
      <SEO
        title={lang === 'da' ? 'Ofte stillede spørgsmål (FAQ)' : 'Frequently Asked Questions (FAQ)'}
        description={lang === 'da'
          ? 'Svar på ofte stillede spørgsmål om massage hos Jette: behandlingsvarighed, gravidmassage, betaling, udekørende behandlinger og hvad du skal medbringe.'
          : 'Answers to frequently asked questions about massage sessions with Jette: treatment duration, pregnancy massage, payment options, and mobile out-calls.'
        }
      />
      
      <section className="py-16 bg-[#FAF7F2] border-b border-[#E8E0D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl space-y-4">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#2E3191] bg-[#EEF0F8] px-3.5 py-1 rounded-full border border-[#2E3191]/20 inline-block">
            FAQ
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-bold text-[#2C2825]">
            {lang === 'da' ? 'Ofte stillede spørgsmål' : 'Frequently Asked Questions'}
          </h1>
          <p className="text-base sm:text-lg text-[#6E6760]">
            {lang === 'da'
              ? 'Find svar på de mest almindelige spørgsmål om behandlinger, priser og tidsbestilling.'
              : 'Find answers to common questions regarding session length, treatments, and booking.'}
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          {faqs.map((faq, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className="bg-[#FAF7F2] rounded-2xl border border-[#E8E0D7] overflow-hidden transition-all"
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : idx)}
                  className="w-full px-6 py-5 text-left font-serif-display text-xl font-bold text-[#2C2825] flex items-center justify-between cursor-pointer hover:text-[#2E3191]"
                >
                  <span>{faq.q[lang]}</span>
                  <i className={`fa-solid fa-chevron-down text-sm transition-transform ${isOpen ? 'rotate-180 text-[#2E3191]' : 'text-[#6E6760]'}`} />
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 pt-2 text-sm text-[#6E6760] leading-relaxed border-t border-[#E8E0D7]/60">
                    {faq.a[lang]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

    </div>
  );
}
